import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Info, Loader2, Scale, TriangleAlert } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Middle, Side } from "@/components/Perspective";

const FactList = ({ rows, testid }) => (
  <ul className="mt-1.5 space-y-1.5">{rows.map((f, i) => (
    <li key={f.fact_id}>
      <Link to={`/facts/${f.fact_id}`} className="group block" data-testid={`${testid}-${i}`}>
        <span className="inline-flex items-start gap-1 font-medium text-slate-900 group-hover:underline">{f.headline}<ArrowUpRight size={12} className="mt-1 shrink-0" /></span>
        {f.why && <span className="block text-xs text-slate-600">{f.why}</span>}
      </Link>
    </li>))}</ul>
);

export const OpinionFacts = ({ p }) => {
  const oc = p.opinion_check;
  if (p.kind !== "opinion" || !oc || (!oc.disputes?.length && !oc.context?.length)) return null;
  const n = oc.disputes?.length || 0;
  return (
    <div className="mt-3 space-y-3 rounded-xl border border-border bg-[#FAF8F5] p-3 text-sm" data-testid={`opinion-facts-${p.id}`}>
      {n > 0 && <div data-testid={`opinion-disputes-${p.id}`}>
        <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-rose-700"><TriangleAlert size={13} />{n} verified Fact{n > 1 ? "s" : ""} dispute{n > 1 ? "" : "s"} this opinion</p>
        <FactList rows={oc.disputes} testid={`opinion-dispute-${p.id}`} />
      </div>}
      {oc.context?.length > 0 && <div data-testid={`opinion-context-${p.id}`}>
        <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-sky-700"><Info size={13} />Important context from verified Facts</p>
        <FactList rows={oc.context} testid={`opinion-context-fact-${p.id}`} />
      </div>}
    </div>
  );
};

const PerspectiveBody = ({ postId }) => {
  const [v, setV] = useState(null);
  useEffect(() => {
    let stop = false;
    const load = async (tries = 0) => {
      try {
        const { data } = await api.get(`/posts/${postId}/perspective`);
        if (stop) return;
        setV(data);
        if (data.state === "generating" && tries < 20) setTimeout(() => load(tries + 1), 4000);
      } catch { if (!stop) setV({ state: "failed" }); }
    };
    load();
    return () => { stop = true; };
  }, [postId]);
  if (!v || v.state === "generating") return <p className="flex items-center gap-2 py-6 text-sm text-slate-500" data-testid="opinion-perspective-loading"><Loader2 size={16} className="animate-spin" />Middle is writing its perspective on this issue (about 20 seconds)...</p>;
  if (v.state !== "ready") return <p className="py-6 text-sm text-slate-500">Couldn't prepare a perspective right now. Try again shortly.</p>;
  return (
    <div className="space-y-3" data-testid="opinion-perspective-ready">
      <p className="text-sm text-slate-500">The issue: <b className="text-slate-800">{v.issue}</b>{v.country && ` · ${v.country}`}</p>
      <div className="grid gap-3 md:grid-cols-2"><Side label={v.left_label} s={v.left} testid="opinion-perspective-left" /><Side label={v.right_label} s={v.right} testid="opinion-perspective-right" /></div>
      <Middle m={v.middle} testid="opinion-perspective-middle" />
      {v.facts?.length > 0 && <p className="flex flex-wrap gap-x-3 gap-y-1 text-xs">{v.facts.map((f) => <Link key={f.id} to={`/facts/${f.id}`} className="text-[hsl(var(--facts))] hover:underline">{f.headline}</Link>)}</p>}
      <p className="text-[11px] text-slate-400">AI-written from verified Facts. Middle doesn't judge the member or endorse any side.</p>
    </div>
  );
};

export const OpinionPerspective = ({ p }) => {
  const [open, setOpen] = useState(false);
  if (p.kind !== "opinion") return null;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={`opinion-perspective-btn-${p.id}`}
        className="mt-3 flex w-full items-center gap-2 rounded-xl border border-[hsl(var(--facts)/0.3)] bg-[hsl(var(--facts)/0.05)] px-3 py-2.5 text-left text-sm font-medium text-[hsl(var(--facts))] transition-colors hover:bg-[hsl(var(--facts)/0.1)]">
        <Scale size={16} />Check the Middle perspective on this opinion<ArrowUpRight size={14} className="ml-auto" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto" data-testid={`opinion-perspective-dialog-${p.id}`}>
          <DialogHeader><DialogTitle className="font-serif text-2xl font-medium">Middle's perspective</DialogTitle>
            <DialogDescription>How the mainstream camps approach the issue this opinion raises, and what the evidence shows.</DialogDescription></DialogHeader>
          {open && <PerspectiveBody postId={p.id} />}
        </DialogContent>
      </Dialog>
    </>
  );
};
